import { RiskEvent, RiskOrder, SourceType } from '../../models';
import i18n from '../../i18n';
import { formatEventTypeText, formatRiskTypeText } from '../../constants/tool-order';

/** 即时与云端风控列表的接口展示模式。 */
export type RiskOrderMode = 'risk' | 'order';

/** 订单模式返回的订单分组结构。 */
export type RiskOrderGroup = RiskOrder & {
    /** 订单下的规则风险点。 */
    riskOrders?: RiskOrder[];
    /** 订单下的事件风险点。 */
    riskEvents?: Array<RiskOrder | RiskEvent>;
    /** 订单下所有风险记录的 ID。 */
    risk_order_ids?: number[];
};

/** 模式化风控响应的统一结构。 */
export interface NormalizedRiskResponse {
    /** 订单模式下的订单分组。 */
    orders: RiskOrderGroup[];
    /** 风险点模式下的规则风险点。 */
    riskOrders: RiskOrder[];
    /** 风险点模式下的事件风险点。 */
    riskEvents: RiskEvent[];
}

/**
 * 将接口响应统一成页面可消费的风险点/订单两套数据。
 * @param response - 即时或云端风控接口响应
 * @returns 统一后的风险数据
 */
export function normalizeInstantRiskResponse(response: Record<string, unknown>, mode: RiskOrderMode = 'order'): NormalizedRiskResponse {
    const riskOrders = (response.riskOrders ?? []) as RiskOrder[];
    const riskEvents = (response.riskEvents ?? []) as RiskEvent[];
    const responseOrders = (response.orders ?? response.data ?? []) as RiskOrderGroup[];

    if (mode === 'risk') {
        return { orders: [], riskOrders, riskEvents };
    }

    if (responseOrders.length > 0) {
        return { orders: responseOrders, riskOrders, riskEvents };
    }

    const alreadyGrouped = riskOrders.filter((item) => {
        const grouped = item as RiskOrderGroup;
        return (
            Boolean(grouped.riskOrders?.length || grouped.riskEvents?.length || grouped.risk_types?.length || grouped.event_types?.length)
        );
    }) as RiskOrderGroup[];
    if (alreadyGrouped.length > 0) {
        return { orders: alreadyGrouped, riskOrders, riskEvents };
    }

    const groups = new Map<number, RiskOrderGroup>();
    riskOrders.forEach((riskOrder) => {
        if (riskOrder.order_head_id === undefined) {
            return;
        }
        const current = groups.get(riskOrder.order_head_id);
        const next = current ?? { ...riskOrder, riskOrders: [], riskEvents: [] };
        next.riskOrders = [...(next.riskOrders ?? []), riskOrder];
        groups.set(riskOrder.order_head_id, next);
    });

    riskEvents.forEach((event) => {
        event.orders?.forEach((eventOrder) => {
            if (eventOrder.order_head_id === undefined) {
                return;
            }
            const current = groups.get(eventOrder.order_head_id);
            const next = current ?? { ...eventOrder, riskOrders: [], riskEvents: [] };
            next.riskEvents = [...(next.riskEvents ?? []), {
                ...eventOrder,
                id: event.id,
                eventId: event.eventId,
                event_type: event.eventType,
                source_type: SourceType.EVENT,
            }];
            groups.set(eventOrder.order_head_id, next);
        });
    });

    return { orders: Array.from(groups.values()), riskOrders, riskEvents };
}

/**
 * 提取订单上的全部风险标签，供订单模式展示。
 * @param order - 订单模式订单
 * @returns 去重后的规则风险类型与事件类型
 */
export function getRiskTagsForOrder(order: RiskOrderGroup): Array<{ type: 'risk' | 'event'; value: number }> {
    const tags = [
        ...(order.risk_types ?? []).map((value) => ({ type: 'risk' as const, value: Number(value) })),
        ...(order.event_types ?? []).map((value) => ({ type: 'event' as const, value: Number(value) })),
        ...(order.risk_type ? [{ type: 'risk' as const, value: Number(order.risk_type) }] : []),
        ...(order.event_type ? [{ type: 'event' as const, value: Number(order.event_type) }] : []),
        ...(order.riskOrders ?? []).flatMap((item) => item.risk_type ? [{ type: 'risk' as const, value: Number(item.risk_type) }] : []),
        ...(order.riskEvents ?? []).flatMap((item) => {
            const event = item as RiskEvent;
            const eventType = event.eventType ?? (item as RiskOrder).event_type;
            return eventType ? [{ type: 'event' as const, value: Number(eventType) }] : [];
        }),
    ];
    return tags.filter((tag, index) =>
        Number.isFinite(tag.value) &&
        tags.findIndex((candidate) => candidate.type === tag.type && candidate.value === tag.value) === index
    );
}

/**
 * 将订单聚合中的全部风险标签转换为导出使用的多语言文本。
 * @param order - 订单模式的聚合订单
 * @returns 去重后的风险标签文本，多个标签使用顿号连接
 */
export function getRiskTagTextForOrder(order: RiskOrderGroup): string {
    return getRiskTagsForOrder(order)
        .map((tag) => i18n.t(tag.type === 'risk' ? formatRiskTypeText(tag.value) : formatEventTypeText(tag.value)))
        .join('、') || '-';
}

/**
 * 根据处理范围提取需要提交的风险记录 ID。
 * @param order - 当前订单或风险点
 * @param scope - ORDER 表示整单，RISK 表示当前风险点
 * @returns 合法且去重后的风险记录 ID
 */
export function getRiskOrderIdsForScope(order: RiskOrderGroup, scope: 'ORDER' | 'RISK'): number[] {
    const ids = scope === 'ORDER'
        ? [
            ...(order.risk_order_ids ?? []),
            ...(order.riskOrders ?? []).map((item) => item.id),
            ...(order.riskEvents ?? []).flatMap((item) => [item.id, (item as RiskEvent).eventId]),
        ]
        : [order.id ?? order.riskOrders?.[0]?.id];
    return Array.from(new Set(ids.filter((id): id is number => typeof id === 'number')));
}

